import { useEffect, useState } from 'react';
import { DataGridLayout, RelatedList, RelationListLayout } from 'framework/list';
import { TextField } from 'framework/ui/field';
import useProcessVariablesHelper from 'domain/gpo/cockpit/instance/Show/variables/processVariablesHelper';
import VariableContextPanel from 'domain/gpo/cockpit/instance/Show/variables/VariableContextPanel';
import { useProcessInstanceId, useProcessInstanceUrl } from 'domain/gpo/cockpit/instance/processInstanceRoute';
import MediumHeading from 'domain/gpo/cockpit/instance/Show/MediumHeading';
import { useSearchParameter } from 'framework/routing';
import { useNavigate } from 'react-router-dom';
import { BpmnDiagramService } from 'domain/gpo/cockpit/facade/useBpmnJs';
import { useShowContext } from 'framework/show';
import { ActivityInstance } from 'domain/gpo/cockpit/instance/processInstanceService';
import AddProcessVariableButton from 'domain/gpo/cockpit/instance/Show/variables/shared/button/AddProcessVariableButton';
import { SimpleTooltip } from 'framework/ui/ui-builder';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

interface Props {
  bpmnJs?: BpmnDiagramService;
}

const ProcessVariables = ({ bpmnJs }: Props) => {
  const processId = useProcessInstanceId();
  const contextFromParams = useSearchParameter('context');
  const [activeContext, setActiveContext] = useState(processId);

  useEffect(() => {
    if (contextFromParams) {
      setActiveContext(contextFromParams);
    }
  }, [contextFromParams]);

  const { data } = useShowContext();
  const activities = data && data.getValueBySource('activities');
  const isDiagramLoaded = bpmnJs?.isLoaded();
  useEffect(() => {
    if (isDiagramLoaded && activities && activeContext !== processId) {
      const { activityId } = activities.find((a: ActivityInstance) => a.executionId === activeContext);
      bpmnJs?.zoomToElement(activityId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeContext, isDiagramLoaded]);

  const { getExecutionVariables } = useProcessVariablesHelper();
  const VariablesList = () => {
    const getDataMethod = async () => getExecutionVariables(activeContext!);
    return (
      <RelatedList getDataMethod={getDataMethod}>
        <RelationListLayout actions={[]}>
          <DataGridLayout showTotal={false} buttons={[]} isSmallTable>
            <TextField source="name" />
            <TextField source="type" />
            <TextField source="value" />
          </DataGridLayout>
        </RelationListLayout>
      </RelatedList>
    );
  };

  const navigate = useNavigate();
  const { getVariablesUrl } = useProcessInstanceUrl();
  const onClick = (context: string) => {
    navigate(getVariablesUrl(processId, context));
  };
  return (
    <Box sx={{ minHeight: '400px' }}>
      <Stack direction="row" justifyContent="space-between">
        <Box sx={{ '& > *': { display: 'inline-block' } }}>
          <MediumHeading>Variables</MediumHeading>
          <SimpleTooltip
            id="history-tooltip"
            isBigText
            sxIcon={{ marginLeft: '0.5em', fontSize: '1.5em', cursor: 'pointer', transform: 'translateY(3px)' }}
          >
            The variables are accessible in different scopes.
            <br />
            <br />
            The top-most scope is global, meaning that the variable is globally accessible and has the same value within
            the entire instance.
            <br />
            <br />
            The tab also contains a node for each activity that has already finished or is currently active in the
            process instance. It allows you to select activity instances (single enactments or activities within a
            process, including their associated data) to explore their details.
          </SimpleTooltip>
        </Box>
        <AddProcessVariableButton />
      </Stack>
      <VariableContextPanel activeContext={activeContext!} setActiveContext={onClick} />
      <VariablesList />
    </Box>
  );
};

export default ProcessVariables;
